import { createContext, useCallback, useContext, useMemo, useState } from 'react';

type Facet = { key: string; label: string; children?: Facet[] };

type ExpandedFacets = Record<string, boolean | undefined>;

interface Context {
	expandedFacets: ExpandedFacets;
	toggleOpen: (facetId: string) => void;
}

export const ExpandContext = createContext<Context | null>(null);

/**
 * Manages the state for expanding collapsing hierarchical facets
 * @param facets
 */
export function useExpandCollapse(facets?: Facet[]): {
	isAnyExpanded: boolean;
	contextValue: Context;
	toggleAll: () => void;
} {
	const [expandedGroups, setExpandedGroups] = useState<ExpandedFacets>({});

	// Detect if any groups are expanded
	const isAnyExpanded = useMemo(
		() => !!Object.values(expandedGroups).reduce((a, b) => Boolean(a || b), false),
		[expandedGroups]
	);

	// create the context value
	const contextValue = useMemo<Context>(() => {
		return {
			expandedFacets: expandedGroups,
			toggleOpen: (facetId) => {
				const copyExpanded = { ...expandedGroups };
				const facet = copyExpanded[facetId];
				if (facet) {
					copyExpanded[facetId] = null;
				} else {
					copyExpanded[facetId] = true;
				}
				setExpandedGroups(copyExpanded);
			},
		};
	}, [expandedGroups]);

	/**
	 * The expected behavior for the expand/collapse all toggle is:
	 *
	 * 1) if all visible groups are closed, Expand All
	 * 2) in any other state (if any single group is open), Collapse All
	 * Expand All should occur recursively - expanding all sub groups
	 */
	const toggleAll = useCallback(() => {
		if (isAnyExpanded) {
			setExpandedGroups({});
		} else {
			let allFacetKeys: string[] = [];
			const recursiveAddFacets = (facets: Facet[] | null) => {
				facets.forEach((facet) => {
					allFacetKeys.push(facet.key);
					const children = facet.children;
					if (children) {
						recursiveAddFacets(children);
					}
				});
			};
			recursiveAddFacets(facets);
			const expanded = allFacetKeys.reduce((facets, key) => ({ ...facets, [key]: true }), {});
			setExpandedGroups(expanded);
		}
	}, [isAnyExpanded, facets]);

	return {
		isAnyExpanded,
		contextValue,
		toggleAll,
	};
}

/**
 * Manages the expanded/collapsed state for any facet given an id.
 * @param id
 */
export function useIsExpanded(id: string): [boolean, () => void] {
	const context: Context | null = useContext(ExpandContext);
	const [isExpandedState, setIsOpen] = useState(false); // use if context is not available
	const isExpandedContext = Boolean(context?.expandedFacets[id]);

	const toggleOpenContext = useCallback(() => {
		context.toggleOpen?.(id);
	}, [context, id]);

	const toggleOpenState = useCallback(() => {
		setIsOpen((x) => !x);
	}, []);

	return context ? [isExpandedContext, toggleOpenContext] : [isExpandedState, toggleOpenState];
}
